import { useCallback, useState } from "react"

import { usePaywallReferralModalStore } from "./PaywallReferralModal"
import { useQuarterlyOfferModalStore } from "./QuarterlyOfferModal"
import { useStripeCheckoutModalStore } from "./StripeCheckoutModal"
import { useFamilyQuery } from "../../networking/queries"
import { SubscriptionCampaign } from "../../types"
import { ANALYTIC_EVENTS, trackAnalyticEvent } from "../../util/analytics"
import { useRouter } from "../../util/router"
import {
  getFamilyBillingLink,
  getFamilyStripeCheckoutLink,
} from "../../util/stripe"

export const useCheckoutRedirect = () => {
  const [isLoadingCheckoutFn, setIsLoadingCheckoutFn] = useState(false)
  const router = useRouter()
  const { hasValidCode } = usePaywallReferralModalStore()
  const { isQuarterlyPlan } = useQuarterlyOfferModalStore()
  const { onOpen: openStripeCheckoutModal } = useStripeCheckoutModalStore()

  const { data: family } = useFamilyQuery()

  const redirectToFamilyBilling = useCallback(async () => {
    if (!family) return

    setIsLoadingCheckoutFn(true)
    const billingLink = await getFamilyBillingLink({ familyId: family.id })

    window.open(billingLink, "_self")
    trackAnalyticEvent(ANALYTIC_EVENTS.SHOW_BILLING)
    setIsLoadingCheckoutFn(false)
  }, [family])

  const redirectToFamilyCheckout = useCallback(
    async (
      paywallCampaign?: string,
      embedded?: boolean,
      applyTrial?: boolean
    ) => {
      if (!family || isLoadingCheckoutFn) return

      setIsLoadingCheckoutFn(true)

      const useEmbedded = embedded !== undefined ? embedded : true

      const campaign =
        paywallCampaign ||
        (isQuarterlyPlan
          ? SubscriptionCampaign.QUARTERLY_PLAN
          : hasValidCode
            ? SubscriptionCampaign.DISCOUNT_ANNUAL
            : router.query.campaign)

      try {
        const result = await getFamilyStripeCheckoutLink({
          familyId: family.id,
          campaign,
          embedded: useEmbedded,
          applyTrial,
        })

        if (
          useEmbedded &&
          result.client_secret &&
          result.stripe_publishable_key
        ) {
          openStripeCheckoutModal(
            result.client_secret,
            result.stripe_publishable_key,
            result.has_trial,
            result.customer_session_client_secret,
            result.trial_days
          )
          trackAnalyticEvent(ANALYTIC_EVENTS.SHOW_CHECKOUT, { campaign })
        } else if (result.checkout_url === null) {
          redirectToFamilyBilling()
        } else if (result.checkout_url) {
          window.open(result.checkout_url, "_self")
          trackAnalyticEvent(ANALYTIC_EVENTS.SHOW_CHECKOUT, { campaign })
        }
      } finally {
        setIsLoadingCheckoutFn(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      router,
      family,
      hasValidCode,
      isLoadingCheckoutFn,
      isQuarterlyPlan,
      openStripeCheckoutModal,
      redirectToFamilyBilling,
    ]
  )

  return {
    isLoadingCheckoutFn,
    redirectToFamilyCheckout,
    redirectToFamilyBilling,
  }
}
